import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  Briefcase,
  MapPin,
  Camera,
  Save,
  Lock,
  Palette,
  Info,
  Shield,
  Moon,
  Sun,
  Volume2,
  VolumeX,
  Vibrate,
  Eye,
  Fingerprint,
  Hash,
  Upload,
  Image as ImageIcon,
  Navigation,
  Smartphone,
  Map
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { storage, db } from '../utils/firebase/client';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { biometricService } from '../utils/security/biometric';
import { authService } from '../utils/auth/authService';
import { updateNavigationMode, updateDefaultMapsApp } from '../utils/userPreferences';

interface UserSettingsProps {
  user: any;
  onClose: () => void;
  onSave: (userData: any) => void;
  onOpenMap?: () => void;
  isDarkMode?: boolean;
}

export default function UserSettings({ user, onClose, onSave, onOpenMap, isDarkMode = false }: UserSettingsProps) {
  const [activeTab, setActiveTab] = useState<'profile' | 'preferences' | 'security' | 'voice'>('profile');
  const [formData, setFormData] = useState({
    email: user.email || '',
    name: user.name || '',
    phone: user.phone || '',
    company: user.company || '',
    location: user.location || '',
    role: user.role || '',
    profilePhoto: user.profilePhoto || '',
    companyLogo: user.companyLogo || '',
  });

  const [preferences, setPreferences] = useState({
    theme: user.preferences?.theme || 'light',
    language: user.preferences?.language || 'en',
    soundEnabled: user.preferences?.soundEnabled ?? true,
    vibrateEnabled: user.preferences?.vibrateEnabled ?? true,
    showTips: user.preferences?.showTips ?? true,
    navigationMode: user.preferences?.navigationMode || 'internal',
    defaultMapsApp: user.preferences?.defaultMapsApp || 'system',
  });

  // Sync preferences state when user prop changes
  useEffect(() => {
    if (user?.preferences) {
      setPreferences(prev => ({
        ...prev,
        theme: user.preferences?.theme || prev.theme,
        language: user.preferences?.language || prev.language,
        soundEnabled: user.preferences?.soundEnabled ?? prev.soundEnabled,
        vibrateEnabled: user.preferences?.vibrateEnabled ?? prev.vibrateEnabled,
        showTips: user.preferences?.showTips ?? prev.showTips,
        navigationMode: user.preferences?.navigationMode || prev.navigationMode,
        defaultMapsApp: user.preferences?.defaultMapsApp || prev.defaultMapsApp,
      }));
    }
  }, [user]);

  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: user.security?.twoFactorEnabled || false,
    pinEnabled: user.security?.pinEnabled || false,
    pin: '',
    confirmPin: '',
    biometricEnabled: user.security?.biometricEnabled || false,
  });

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePreferenceChange = async (field: string, value: any) => {
    setPreferences(prev => ({ ...prev, [field]: value }));

    // Immediately save navigation preferences to database
    const companyId = user?.companyId || user?.companyId || 'default';

    if (field === 'navigationMode') {
      try {
        await updateNavigationMode(user.id, value, companyId);
        toast.success(`Navigation mode set to ${value === 'internal' ? 'In-App' : 'External'}`);
      } catch (error) {
        console.error('Failed to update navigation mode:', error);
      }
    }

    if (field === 'defaultMapsApp') {
      try {
        await updateDefaultMapsApp(user.id, value, companyId);
        const appName = value === 'system' ? 'System Default' :
          value === 'google' ? 'Google Maps' :
            value === 'apple' ? 'Apple Maps' :
              value === 'waze' ? 'Waze' : value;
        toast.success(`Default maps app set to ${appName}`);
      } catch (error) {
        console.error('Failed to update default maps app:', error);
      }
    }
  };

  const handleSecurityChange = (field: string, value: any) => {
    setSecurity(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        handleChange('profilePhoto', event.target.result as string);
        toast.success('Profile photo updated');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be less than 5MB');
      return;
    }

    try {
      toast.loading('Uploading company logo...');

      // Get tenant/company slug from user
      const companyId = user.companyId || 'default';

      // Create storage reference - using requested path structure
      const logoRef = ref(storage, `companies/${companyId}/images/logo.png`);

      // Upload file
      await uploadBytes(logoRef, file);

      // Get download URL
      const logoUrl = await getDownloadURL(logoRef);

      // Update local state
      handleChange('companyLogo', logoUrl);

      // Update company document in Firestore immediately for the logo
      const companiesRef = collection(db, 'companies');
      const q = query(companiesRef, where('companyId', '==', companyId));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const companyDoc = snapshot.docs[0];
        await updateDoc(doc(db, 'companies', companyDoc.id), {
          'branding.logo': logoUrl,
          'branding.logoUrl': logoUrl,
          updatedAt: new Date()
        });
      }

      toast.dismiss();
      toast.success('Company logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.dismiss();
      toast.error('Failed to upload logo. Please try again.');
    }
  };

  const handleSaveProfile = async () => {
    if (!formData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    // Validate PIN if enabled
    if (security.pinEnabled && security.pin && security.pin.length !== 4) {
      toast.error('PIN must be 4 digits');
      return;
    }

    if (security.pinEnabled && security.pin !== security.confirmPin) {
      toast.error('PINs do not match');
      return;
    }

    // Save or clear PIN via authService for central handling
    if (security.pinEnabled && security.pin) {
      const ok = await authService.updatePin(security.pin);
      if (!ok) {
        toast.error('Failed to save PIN');
        return;
      }
    } else if (!security.pinEnabled && user.security?.pinEnabled) {
      // User disabled PIN - clear it using authService
      await authService.updatePin('');
    }

    try {
      toast.loading('Saving profile...');

      // Update user document in Firestore via authService (handles security subcollection)
      await authService.updateUserProfile(user.id, {
        name: formData.name,
        phone: formData.phone,
        company: formData.company,
        location: formData.location,
        profilePhoto: formData.profilePhoto,
        companyLogo: formData.companyLogo,
        preferences: preferences,
        security: {
          twoFactorEnabled: security.twoFactorEnabled,
          pinEnabled: security.pinEnabled,
          biometricEnabled: security.biometricEnabled,
        }
      });

      // If user is a manager/admin, update the Company document as well
      if (user.role === 'admin' || user.role === 'manager' || user.role === 'superadmin') {
        const companyId = user.companyId || 'default';
        const companiesRef = collection(db, 'companies');
        const q = query(companiesRef, where('companyId', '==', companyId));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const companyDoc = snapshot.docs[0];
          await updateDoc(doc(db, 'companies', companyDoc.id), {
            displayName: formData.company, // Dynamic display name
            companyName: formData.company, // Keep legacy field in sync
            'branding.logo': formData.companyLogo,
            updatedAt: new Date()
          });
        }
      }

      const updatedUser = {
        ...user,
        ...formData,
        preferences,
        security: {
          ...user.security,
          twoFactorEnabled: security.twoFactorEnabled,
          pinEnabled: security.pinEnabled,
          pin: security.pin || user.security?.pin,
          biometricEnabled: security.biometricEnabled,
        },
        companyLogo: formData.companyLogo,
      };

      // Update localStorage immediately so lock screen can use it
      const authSessionKey = 'auth_session';
      localStorage.setItem(authSessionKey, JSON.stringify(updatedUser));

      onSave(updatedUser);

      toast.dismiss();
      toast.success('Profile saved successfully to database!');
      onClose();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.dismiss();
      toast.error('Failed to save profile. Please try again.');
    }
  };

  const handleChangePassword = () => {
    if (!security.currentPassword || !security.newPassword || !security.confirmPassword) {
      toast.error('All password fields are required');
      return;
    }

    if (security.newPassword !== security.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (security.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    // In production, validate current password and update
    toast.success('Password changed successfully');
    setSecurity({
      ...security,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${isDarkMode ? 'bg-slate-950' : 'bg-white'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold">User Settings</h2>
            <p className="text-sm text-blue-100 mt-1">Manage your account and preferences</p>
          </div>
          <button
            type="button"
            title="Close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${activeTab === 'profile'
              ? 'bg-white text-blue-700 shadow-md'
              : 'bg-white/20 text-white hover:bg-white/30'
              }`}
          >
            <User className="h-4 w-4 inline mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('preferences')}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${activeTab === 'preferences'
              ? 'bg-white text-blue-700 shadow-md'
              : 'bg-white/20 text-white hover:bg-white/30'
              }`}
          >
            <Palette className="h-4 w-4 inline mr-2" />
            Preferences
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${activeTab === 'security'
              ? 'bg-white text-blue-700 shadow-md'
              : 'bg-white/20 text-white hover:bg-white/30'
              }`}
          >
            <Lock className="h-4 w-4 inline mr-2" />
            Security
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <>
            {/* Profile Photo */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Profile Photo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    {formData.profilePhoto ? (
                      <ImageWithFallback
                        src={formData.profilePhoto}
                        alt="Profile"
                        className="h-20 w-20 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <User className="h-10 w-10 text-white" />
                      </div>
                    )}
                    <label className="absolute bottom-0 right-0 p-1.5 bg-blue-600 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
                      <Camera className="h-3.5 w-3.5 text-white" />
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        aria-label="Upload profile photo"
                      />
                    </label>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{formData.name || 'Add your name'}</p>
                    <p className="text-sm text-gray-600">{formData.email}</p>
                    <Badge className="mt-2 bg-blue-100 text-blue-800">{formData.role}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <User className="h-4 w-4 mr-2 text-gray-500" />
                    Full Name
                  </label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Mail className="h-4 w-4 mr-2 text-gray-500" />
                    Email Address
                  </label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-gray-500" />
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="h-12"
                  />
                </div>

                {/* Company Settings - Manager Only */}
                {(user.role === 'admin' || user.role === 'manager' || user.role === 'superadmin') && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Briefcase className="h-4 w-4 mr-2 text-gray-500" />
                      Company Display Name
                    </label>
                    <Input
                      type="text"
                      placeholder="ABC Oil & Gas"
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      className="h-12"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This name will be displayed in the header for all users in your organization.
                    </p>
                  </div>
                )}

                {/* Company Logo - Manager/Admin Only */}
                {(user.role === 'admin' || user.role === 'manager' || user.role === 'superadmin') && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <ImageIcon className="h-4 w-4 mr-2 text-gray-500" />
                      Company Logo
                    </label>
                    <div className="flex items-center space-x-4">
                      {formData.companyLogo ? (
                        <div className="relative">
                          <img
                            src={formData.companyLogo}
                            alt="Company Logo"
                            className="h-16 w-32 object-contain rounded-lg border-2 border-gray-200 bg-white p-2"
                          />
                          <button
                            type="button"
                            onClick={() => handleChange('companyLogo', '')}
                            className="absolute -top-2 -right-2 p-1 bg-red-600 rounded-full hover:bg-red-700 transition-colors shadow-lg"
                            aria-label="Remove company logo"
                          >
                            <X className="h-3 w-3 text-white" />
                          </button>
                        </div>
                      ) : (
                        <div className="h-16 w-32 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                          <ImageIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <label className="cursor-pointer">
                          <div className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex">
                            <Upload className="h-4 w-4" />
                            <span className="text-sm font-medium">Upload Logo</span>
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            aria-label="Upload company logo"
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-2">
                          Replaces the OilWellX logo in the header. Max 5MB. PNG or JPG recommended.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                    Location
                  </label>
                  <Input
                    type="text"
                    placeholder="Houston, TX"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    className="h-12"
                    disabled={user.role !== 'admin' && user.role !== 'manager' && user.role !== 'superadmin'}
                  />
                  {user.role !== 'admin' && user.role !== 'manager' && user.role !== 'superadmin' && (
                    <p className="text-xs text-gray-500 mt-1">Only managers can edit company location</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <>
            {/* Appearance */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Palette className="h-5 w-5 mr-2 text-pink-600" />
                  Appearance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-3 block">Theme</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handlePreferenceChange('theme', 'light')}
                      aria-label="Switch to light theme"
                      className={`p-4 rounded-lg border transition-all ${preferences.theme === 'light'
                        ? 'border-blue-600 bg-white text-blue-700 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                    >
                      <Sun className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">Light</p>
                    </button>
                    <button
                      onClick={() => handlePreferenceChange('theme', 'dark')}
                      aria-label="Switch to dark theme"
                      className={`p-4 rounded-lg border transition-all ${preferences.theme === 'dark'
                        ? 'border-blue-600 bg-zinc-900 text-white shadow-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                    >
                      <Moon className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">Dark</p>
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sound & Haptics */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Volume2 className="h-5 w-5 mr-2 text-blue-600" />
                  Sound & Haptics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {preferences.soundEnabled ? (
                      <Volume2 className="h-5 w-5 text-blue-600" />
                    ) : (
                      <VolumeX className="h-5 w-5 text-gray-400" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">Sound Effects</p>
                      <p className="text-xs text-gray-600">Play sounds for actions</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('soundEnabled', !preferences.soundEnabled)}
                    className={`relative w-14 h-8 rounded-full transition-colors ${preferences.soundEnabled ? 'bg-blue-600' : 'bg-gray-300'
                      } focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2`}
                    aria-label={`Sound effects ${preferences.soundEnabled ? 'enabled' : 'disabled'}. Click to ${preferences.soundEnabled ? 'disable' : 'enable'}`}
                  >
                    <div
                      className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${preferences.soundEnabled ? 'translate-x-7' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Vibrate className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-gray-900">Haptic Feedback</p>
                      <p className="text-xs text-gray-600">Vibrate on interactions</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('vibrateEnabled', !preferences.vibrateEnabled)}
                    className={`relative w-14 h-8 rounded-full transition-colors ${preferences.vibrateEnabled ? 'bg-purple-600' : 'bg-gray-300'
                      } focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2`}
                    aria-label={`Haptic feedback ${preferences.vibrateEnabled ? 'enabled' : 'disabled'}. Click to ${preferences.vibrateEnabled ? 'disable' : 'enable'}`}
                  >
                    <div
                      className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${preferences.vibrateEnabled ? 'translate-x-7' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Other Preferences */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Info className="h-5 w-5 mr-2 text-green-600" />
                  Other Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Eye className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="font-medium text-gray-900">Show Tips</p>
                      <p className="text-xs text-gray-600">Display helpful hints</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handlePreferenceChange('showTips', !preferences.showTips)}
                    className={`relative w-14 h-8 rounded-full transition-colors ${preferences.showTips ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    aria-label={`Show tips ${preferences.showTips ? 'enabled' : 'disabled'}. Click to ${preferences.showTips ? 'disable' : 'enable'}`}
                  >
                    <div
                      className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${preferences.showTips ? 'translate-x-7' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Navigation Preferences */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Navigation className="h-5 w-5 mr-2 text-blue-600" />
                  Navigation & Maps
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-3 block">Navigation Mode</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handlePreferenceChange('navigationMode', 'internal')}
                      className={`p-4 rounded-lg border transition-all ${preferences.navigationMode === 'internal'
                        ? 'border-blue-600 bg-white text-blue-700 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                    >
                      <Navigation className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">In-App</p>
                      <p className="text-xs text-gray-600 mt-1">Stay in OilWellX</p>
                    </button>
                    <button
                      onClick={() => handlePreferenceChange('navigationMode', 'external')}
                      className={`p-4 rounded-lg border transition-all ${preferences.navigationMode === 'external'
                        ? 'border-green-600 bg-white text-green-700 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                    >
                      <Smartphone className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="font-medium text-gray-900">External App</p>
                      <p className="text-xs text-gray-600 mt-1">Use maps app</p>
                    </button>
                  </div>
                </div>

                {preferences.navigationMode === 'external' && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-3 block">Default Maps App</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handlePreferenceChange('defaultMapsApp', 'google')}
                        className={`p-3 rounded-lg border-2 transition-all ${preferences.defaultMapsApp === 'google'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <Map className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                        <p className="text-xs font-medium text-gray-900">Google Maps</p>
                      </button>
                      <button
                        onClick={() => handlePreferenceChange('defaultMapsApp', 'apple')}
                        className={`p-3 rounded-lg border-2 transition-all ${preferences.defaultMapsApp === 'apple'
                          ? 'border-gray-600 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <Navigation className="h-6 w-6 text-gray-600 mx-auto mb-1" />
                        <p className="text-xs font-medium text-gray-900">Apple Maps</p>
                      </button>
                      <button
                        onClick={() => handlePreferenceChange('defaultMapsApp', 'waze')}
                        className={`p-3 rounded-lg border-2 transition-all ${preferences.defaultMapsApp === 'waze'
                          ? 'border-orange-600 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <Smartphone className="h-6 w-6 text-orange-600 mx-auto mb-1" />
                        <p className="text-xs font-medium text-gray-900">Waze</p>
                      </button>
                      <button
                        onClick={() => handlePreferenceChange('defaultMapsApp', 'system')}
                        className={`p-3 rounded-lg border-2 transition-all ${preferences.defaultMapsApp === 'system'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <Smartphone className="h-6 w-6 text-green-600 mx-auto mb-1" />
                        <p className="text-xs font-medium text-gray-900">System Default</p>
                      </button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Map Shortcut */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Map className="h-5 w-5 mr-2 text-purple-600" />
                  Command Center
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Access the full-screen Command Center map with fleet tracking and satellite imagery.
                </p>
                <Button
                  onClick={onOpenMap}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={!onOpenMap}
                >
                  <Map className="h-4 w-4 mr-2" />
                  Open Command Center Map
                </Button>
              </CardContent>
            </Card>

          </>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <>
            {/* Change Password */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Lock className="h-5 w-5 mr-2 text-red-600" />
                  Change Password
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Current Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Enter current password"
                    value={security.currentPassword}
                    onChange={(e) => handleSecurityChange('currentPassword', e.target.value)}
                    className="h-12"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    New Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Enter new password"
                    value={security.newPassword}
                    onChange={(e) => handleSecurityChange('newPassword', e.target.value)}
                    className="h-12"
                  />
                  <p className="text-xs text-gray-600 mt-1">Must be at least 8 characters</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Confirm New Password
                  </label>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    value={security.confirmPassword}
                    onChange={(e) => handleSecurityChange('confirmPassword', e.target.value)}
                    className="h-12"
                  />
                </div>

                <Button
                  onClick={handleChangePassword}
                  className="w-full h-12 bg-red-600 hover:bg-red-700"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Change Password
                </Button>
              </CardContent>
            </Card>

            {/* Two-Factor Authentication */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-green-600" />
                  Two-Factor Authentication
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Enable 2FA</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Add an extra layer of security to your account
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      handleSecurityChange('twoFactorEnabled', !security.twoFactorEnabled);
                      toast.success(
                        security.twoFactorEnabled
                          ? '2FA disabled'
                          : '2FA enabled - Check your email for setup instructions'
                      );
                    }}
                    className={`relative w-14 h-8 rounded-full transition-colors ${security.twoFactorEnabled ? 'bg-green-600' : 'bg-gray-300'
                      }`}
                    aria-label={`Two-factor authentication ${security.twoFactorEnabled ? 'enabled' : 'disabled'}. Click to ${security.twoFactorEnabled ? 'disable' : 'enable'}`}
                  >
                    <div
                      className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${security.twoFactorEnabled ? 'translate-x-7' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* PIN Setup */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Hash className="h-5 w-5 mr-2 text-blue-600" />
                  PIN Code
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Enable PIN</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Quick access with 4-digit PIN
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label={security.pinEnabled ? 'Disable PIN authentication' : 'Enable PIN authentication'}
                    onClick={() => {
                      if (!security.pinEnabled) {
                        // Show PIN setup fields
                        handleSecurityChange('pinEnabled', true);
                      } else {
                        handleSecurityChange('pinEnabled', false);
                        handleSecurityChange('pin', '');
                        handleSecurityChange('confirmPin', '');
                        toast.success('PIN disabled');
                      }
                    }}
                    className={`relative w-14 h-8 rounded-full transition-colors ${security.pinEnabled ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                  >
                    <div
                      className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${security.pinEnabled ? 'translate-x-7' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>

                {security.pinEnabled && (
                  <div className="space-y-3 pt-2 border-t">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Enter 4-Digit PIN
                      </label>
                      <Input
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="Enter PIN"
                        value={security.pin}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          handleSecurityChange('pin', value);
                        }}
                        className="h-12 text-center text-2xl tracking-widest"
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Confirm PIN
                      </label>
                      <Input
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="Confirm PIN"
                        value={security.confirmPin}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          handleSecurityChange('confirmPin', value);
                        }}
                        className="h-12 text-center text-2xl tracking-widest"
                      />
                    </div>

                    <Button
                      onClick={() => {
                        if (security.pin.length !== 4) {
                          toast.error('PIN must be 4 digits');
                          return;
                        }
                        if (security.pin !== security.confirmPin) {
                          toast.error('PINs do not match');
                          return;
                        }
                        // Don't clear the PIN - let it stay so it gets saved with the profile
                        toast.success('PIN verified - click Save at the bottom to save all changes');
                      }}
                      className="w-full h-10 bg-blue-600 hover:bg-blue-700"
                      disabled={!security.pin || !security.confirmPin}
                    >
                      Verify PIN
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Facial Recognition / Biometric */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Fingerprint className="h-5 w-5 mr-2 text-purple-600" />
                  Biometric Authentication
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Face ID / Touch ID</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Use facial recognition or fingerprint to unlock
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!security.biometricEnabled) {
                        // Check if biometric is available
                        if (window.PublicKeyCredential) {
                          try {
                            const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
                            if (available) {
                              // Actually register the biometric credential with the device
                              const result = await biometricService.register(user.id, user.email);

                              if (result.success) {
                                handleSecurityChange('biometricEnabled', true);
                                toast.success('Biometric authentication enabled successfully');
                              } else {
                                toast.error(result.error || 'Failed to enable biometric authentication');
                                console.error('❌ Biometric registration failed:', result.error);
                              }
                            } else {
                              toast.error('Biometric authentication not available on this device');
                            }
                          } catch (error) {
                            console.error('❌ Biometric error:', error);
                            toast.error('Could not enable biometric authentication');
                          }
                        } else {
                          toast.error('Biometric authentication not supported by your browser');
                        }
                      } else {
                        // Disable biometric and remove credential
                        biometricService.removeCredential();
                        handleSecurityChange('biometricEnabled', false);
                        toast.success('Biometric authentication disabled');
                      }
                    }}
                    className={`relative w-14 h-8 rounded-full transition-colors ${security.biometricEnabled ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                    aria-label={`Biometric authentication ${security.biometricEnabled ? 'enabled' : 'disabled'}. Click to ${security.biometricEnabled ? 'disable' : 'enable'}`}
                  >
                    <div
                      className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${security.biometricEnabled ? 'translate-x-7' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>

                {security.biometricEnabled && (
                  <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-start space-x-2">
                      <Shield className="h-4 w-4 text-purple-600 mt-0.5" />
                      <div className="text-xs text-purple-900">
                        <p className="font-medium">Biometric authentication is active</p>
                        <p className="text-purple-700 mt-1">
                          You can now use Face ID, Touch ID, or Windows Hello to quickly sign in.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Session Information */}
            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  <Info className="h-5 w-5 mr-2 text-gray-600" />
                  Session Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Last Login:</span>
                  <span className="font-medium text-gray-900">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Account Created:</span>
                  <span className="font-medium text-gray-900">Jan 15, 2024</span>
                </div>
                <div className="flex justify-between">
                  <span>Active Sessions:</span>
                  <span className="font-medium text-gray-900">1 device</span>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Footer Actions */}
      <div className="border-t p-4 bg-white space-y-3">
        <Button
          onClick={handleSaveProfile}
          className="w-full h-12 bg-blue-600 hover:bg-blue-700"
        >
          <Save className="h-5 w-5 mr-2" />
          Save Changes
        </Button>
        <Button onClick={onClose} variant="outline" className="w-full h-12">
          Cancel
        </Button>
      </div>
    </div>
  );
}
